import React, { useState, useEffect } from 'react';
import { RefreshCw, Check, AlertCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { SECTION_LIMITS } from '@/types/chatCompletion';

interface ChatSummarySectionProps {
  id: string;
  title: string;
  content: string;
  placeholder?: string;
  minLength?: number;
  maxLength?: number;
  required?: boolean;
  onEdit: (newContent: string) => void;
  onRegenerate: () => void;
  isRegenerating?: boolean;
}

export function ChatSummarySection({
  id,
  title,
  content,
  placeholder,
  minLength = SECTION_LIMITS.minLength,
  maxLength = SECTION_LIMITS.maxLength,
  required = false,
  onEdit,
  onRegenerate,
  isRegenerating = false,
}: ChatSummarySectionProps) {
  const [isEditing, setIsEditing] = useState(false);
  const [editedContent, setEditedContent] = useState(content);
  const [hasChanges, setHasChanges] = useState(false);

  useEffect(() => {
    setEditedContent(content);
    setHasChanges(false);
  }, [content]);

  const handleSave = () => {
    if (editedContent.trim().length < minLength) {
      return;
    }
    onEdit(editedContent.trim());
    setIsEditing(false);
    setHasChanges(false);
  };

  const handleCancel = () => {
    setEditedContent(content);
    setIsEditing(false);
    setHasChanges(false);
  };

  const handleContentChange = (value: string) => {
    setEditedContent(value);
    setHasChanges(value !== content);
  };

  const isValidLength = editedContent.trim().length >= minLength &&
                       editedContent.trim().length <= maxLength;

  const getCharacterCountClass = () => {
    const length = editedContent.trim().length;
    if (length < minLength) return 'text-destructive';
    if (length > maxLength - 50) return 'text-warning';
    return 'text-muted-foreground';
  };

  return (
    <div className="border rounded-lg p-4 space-y-3 transition-all hover:border-primary/50">
      <div className="flex items-center justify-between">
        <h3 className="font-semibold text-base">
          {title}
          {required && <span className="text-destructive ml-1">*</span>}
        </h3>
        <div className="flex items-center gap-2">
          {hasChanges && (
            <span className="text-xs text-muted-foreground">Unsaved changes</span>
          )}
          <Button
            variant="ghost"
            size="sm"
            onClick={onRegenerate}
            disabled={isRegenerating || isEditing}
            className="h-8 px-2"
          >
            <RefreshCw className={`h-4 w-4 ${isRegenerating ? 'animate-spin' : ''}`} />
            <span className="ml-1 hidden sm:inline">Regenerate</span>
          </Button>
        </div>
      </div>

      {isEditing ? (
        <div className="space-y-2">
          <Textarea
            value={editedContent}
            onChange={(e) => handleContentChange(e.target.value)}
            className="min-h-[100px] resize-y"
            placeholder={placeholder || `Enter ${title.toLowerCase()}...`}
            autoFocus
          />
          <div className="flex items-center justify-between">
            <span className={`text-xs ${getCharacterCountClass()}`}>
              {editedContent.trim().length} / {maxLength} characters
              {editedContent.trim().length < minLength && 
                ` (min ${minLength})`
              }
            </span>
            <div className="flex gap-2">
              <Button
                variant="ghost"
                size="sm"
                onClick={handleCancel}
                className="h-8"
              >
                Cancel
              </Button>
              <Button
                variant="default"
                size="sm"
                onClick={handleSave}
                disabled={!isValidLength || !hasChanges}
                className="h-8"
              >
                <Check className="h-4 w-4 mr-1" />
                Save
              </Button>
            </div>
          </div>
          {!isValidLength && editedContent.trim().length > 0 && (
            <div className="flex items-center gap-2 text-xs text-destructive">
              <AlertCircle className="h-3 w-3" />
              <span>
                Content must be between {minLength} and {maxLength} characters
              </span>
            </div>
          )}
        </div>
      ) : (
        <div
          className="text-sm text-muted-foreground whitespace-pre-wrap cursor-pointer p-3 rounded-md bg-secondary/20 hover:bg-secondary/30 transition-colors"
          onClick={() => setIsEditing(true)}
        >
          {content || <span className="italic">Click to add content...</span>}
        </div>
      )}
    </div>
  );
}